import { Flex, Spinner, Stack, useToast } from "@chakra-ui/react";
import html2canvas from "html2canvas";
import { useEffect, useRef, useState } from "react";
import COLOR from "../../constant/COLOR";
import IMAGES from "../../constant/IMAGES";
import { getCalendar } from "../../service";
import formatLuna from "../../utils/formatLuna";
import CalendarUpload from "./CalendarUpload";
import Section1 from "./Section1";
import Section2 from "./Section2";
import {
  Text,
  Title,
  WrapperCalendar,
  WrapperDay,
  WrapperZodiac,
} from "./style";
type CalendarList = {
  dateSolar: string;
  dateLunar: string;
  daySolar: number;
  monthSolar: number;
  yearSolar: number;
  dayLunar: number;
  monthLunar: number;
  yearLunar: number;
  ngayCan: string;
  ngayChi: string;
  thangCan: string;
  thangChi: string;
  namCan: string;
  namChi: string;
  truc: string;
  tu: string;
  hanhNgay: string;
  hanhThang: string;
  isLeap: boolean;
  weekday: string;
  gioHoangDao: {
    order: number;
    name: string;
    start: number;
    end: number;
    nguHanh: string;
  }[];
  gioThoTu: {
    order: number;
    name: string;
    start: number;
    end: number;
    nguHanh: string;
  };
  gioSatChu: {
    order: number;
    name: string;
    start: number;
    end: number;
    nguHanh: string;
  };
  tuoiXungNgay: {
    order: number;
    key: string;
    nguHanh: string;
  }[];
  tuoiXungThang: {
    order: number;
    key: string;
    nguHanh: string;
  }[];
  tiet: string;
  bachKy: string[];
  isHaveEvents: boolean;
  events: any[];
  isHoangDaoDay: boolean;
  hoangDaoHacDao: {
    isDayHoangDaoHacDao: boolean;
    isHoangDaoDay: boolean;
  };
};
const canchiImages = [
  IMAGES.z1,
  IMAGES.z2,
  IMAGES.z3,
  IMAGES.z4,
  IMAGES.z5,
  IMAGES.z6,
  IMAGES.z7,
  IMAGES.z8,
  IMAGES.z9,
  IMAGES.z10,
  IMAGES.z11,
  IMAGES.z12,
];
const nguHanhColorMap: { [key: string]: string } = {
  kim: COLOR.primary400,
  moc: COLOR.green700,
  thuy: COLOR.blue,
  hoa: COLOR.accent600,
  tho: COLOR.primary800,
};
const CalendarDetail = () => {
  const [detailCalendar, setDetailCalendar] = useState<CalendarList | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const stackRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const handleGetCalendar = async (date: Date | null) => {
    if (!date) return;

    try {
      const formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}/${date.getFullYear()}`;
      const calendarData = await getCalendar(formattedDate);
      setDetailCalendar(calendarData);
    } catch (error) {
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể lấy dữ liệu lịch. Vui lòng thử lại.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    handleGetCalendar(selectedDate);
  }, [selectedDate]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };
  const captureAndDownload = async () => {
    if (stackRef.current) {
      setIsLoading(true);

      try {
        const canvas = await html2canvas(stackRef.current, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
        });

        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = canvas.width;
        resizedCanvas.height = canvas.height;
        const ctx = resizedCanvas.getContext("2d");

        if (ctx) {
          ctx.drawImage(canvas, 0, 0);

          resizedCanvas.toBlob((blob) => {
            if (blob) {
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);

              const fileName = `phongthuydainam-lichdungsu`;
              link.download = fileName + `.png`;
              link.click();
              URL.revokeObjectURL(link.href);
            }
            toast({
              title: "Tải xuống thành công.",
              description: "Hình ảnh đã được lưu vào thiết bị của bạn.",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
            setIsLoading(false);
          }, "image/png");
        } else {
          throw new Error("Không thể lấy bối cảnh 2D từ canvas");
        }
      } catch (error) {
        toast({
          title: "Đã xảy ra lỗi.",
          description: "Không thể chụp màn hình.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
      }
    } else {
      toast({
        title: "Không tìm thấy nội dung.",
        description: "Không tìm thấy nội dung nào để chụp.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };
  const handleShare = (platform: string) => {
    if (platform === "download") {
      captureAndDownload();
    } else {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CalendarUpload
        detailCalendar={detailCalendar}
        handleShare={handleShare}
        isLoading={isLoading}
        handleDateChange={handleDateChange}
        selectedDate={selectedDate}
        handleGetCalendar={handleGetCalendar}
      />
      <Stack
        sx={{
          overflow: "hidden",
          position: "absolute",
          opacity: 0,
          top: "-1000%",
          left: "-1000%",
        }}
      >
        <Stack
          ref={stackRef}
          sx={{
            backgroundImage: `url(${IMAGES.bgBig})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "repeat",
            height: "2666px",
            width: "2000px",
            position: "relative",
          }}
        >
          <Stack sx={{ width: "100%" }}>
            <Stack gap="68px" mt="140px" padding="0 80px">
              <Flex justifyContent="space-between" width="100%">
                <img src={IMAGES.xheroLogo} alt="" style={{ width: "200px" }} />
                <img
                  src={IMAGES.ptLogo}
                  alt=""
                  style={{ width: "277px", height: "auto" }}
                />
              </Flex>
              <Flex justifyContent="center">
                <img src={IMAGES.textC} alt="" style={{ width: "700px" }} />
              </Flex>

              <WrapperCalendar>
                {detailCalendar ? (
                  <Flex
                    mt="32px"
                    justifyContent="space-between"
                    width="100%"
                    gap="33.45px"
                  >
                    <Section1 detailCalendar={detailCalendar} />
                    <Section2 detailCalendar={detailCalendar} />
                  </Flex>
                ) : (
                  <Spinner
                    color="red.500"
                    css={{ "--spinner-track-color": "colors.gray.200" }}
                    borderWidth="4px"
                    size="lg"
                  />
                )}
              </WrapperCalendar>
              <Flex width="100%" justifyContent="space-between" gap="56px">
                <WrapperZodiac>
                  <Stack gap="32px" width="100%" mb="20px">
                    <Title style={{ textAlign: "center" }}>
                      Tuổi xung ngày
                    </Title>
                    <Stack
                      sx={{
                        background: COLOR.primary300,
                        height: "1px",
                        width: "100%",
                      }}
                    />

                    <Flex
                      padding="0 32px"
                      gap="16px"
                      justifyContent="space-between"
                      mb="50px"
                    >
                      {detailCalendar?.tuoiXungNgay.map(
                        (day: any, idx: any) => (
                          <Stack width="100%">
                            <Stack
                              key={idx}
                              alignItems="center"
                              justifyContent="center"
                              width="100%"
                              gap="32px"
                            >
                              <img
                                src={canchiImages[day?.order - 1]}
                                alt=""
                                style={{ height: "auto", width: "102px" }}
                              />
                              <Title
                                style={{
                                  color: COLOR.primary900,
                                  transform: "translateY(-30px)",
                                }}
                              >{`${formatLuna[day?.key.toLowerCase()]}`}</Title>
                              <WrapperDay>
                                <Text
                                  style={{
                                    color:
                                      nguHanhColorMap[
                                        day?.nguHanh.toLowerCase()
                                      ],
                                    fontSize: "40px",
                                    fontWeight: 600,
                                    transform: "translateY(-20px)",
                                  }}
                                >
                                  {`${formatLuna[day?.nguHanh.toLowerCase()]}`}
                                </Text>
                              </WrapperDay>
                            </Stack>
                            <Stack
                              sx={{
                                background: COLOR.primary300,
                                height: "100%",
                                width: "1px",
                                display: idx === 1 ? "none" : "flex",
                              }}
                            />
                          </Stack>
                        )
                      )}
                    </Flex>
                  </Stack>
                </WrapperZodiac>
                <WrapperZodiac>
                  <Stack gap="32px" width="100%" mb="20px">
                    <Title style={{ textAlign: "center" }}>
                      Tuổi xung tháng
                    </Title>
                    <Stack
                      sx={{
                        background: COLOR.primary300,
                        height: "1px",
                        width: "100%",
                      }}
                    />

                    <Flex
                      padding="0 32px"
                      gap="16px"
                      justifyContent="space-between"
                      width="100%"
                      mb="50px"
                    >
                      {detailCalendar?.tuoiXungThang.map(
                        (day: any, idx: any) => (
                          <Stack width="100%">
                            <Stack
                              key={idx}
                              alignItems="center"
                              justifyContent="center"
                              gap="32px"
                              width="100%"
                            >
                              <img
                                src={canchiImages[day?.order - 1]}
                                alt=""
                                style={{ height: "auto", width: "102px" }}
                              />
                              <Title
                                style={{
                                  color: COLOR.primary900,
                                  transform: "translateY(-30px)",
                                }}
                              >{`${formatLuna[day?.key.toLowerCase()]}`}</Title>
                              <WrapperDay>
                                <Text
                                  style={{
                                    color:
                                      nguHanhColorMap[
                                        day?.nguHanh.toLowerCase()
                                      ],
                                    fontSize: "40px",
                                    fontWeight: 600,
                                    transform: "translateY(-20px)",
                                  }}
                                >
                                  {`${formatLuna[day?.nguHanh.toLowerCase()]}`}
                                </Text>
                              </WrapperDay>
                            </Stack>
                            <Stack
                              sx={{
                                background: COLOR.primary300,
                                height: "100%",
                                width: "1px",
                                display: idx === 1 ? "none" : "flex",
                              }}
                            />
                          </Stack>
                        )
                      )}
                    </Flex>
                  </Stack>
                </WrapperZodiac>
              </Flex>
            </Stack>
            <Flex
              sx={{
                position: "absolute",
                zIndex: 99,
                bottom: 0,
              }}
            >
              <img src={IMAGES.sanh} alt="" style={{ width: "100%" }} />
            </Flex>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export default CalendarDetail;
